<script setup lang="ts">
import {nextTick, ref, watch} from 'vue'
import HomeSearchComponent from '@toolify/client/src/modules/home/components/HomeSearchComponent.vue'
import {useAppLayoutStore} from '@toolify/client/src/stores/AppLayoutStore/useAppLayoutStore'
import {useOverlay} from '@toolify/client/src/modules/modals/composables/useOverlay'

interface Props {
  hasNativeHeader: boolean
}

const props = defineProps<Props>()

const appLayoutStore = useAppLayoutStore()

const rootRef = ref<HTMLElement | null>(null)
const present = ref(false)
const show = ref(false)

const leftAfterTransition = ref({
  overlay: false,
  container: false,
})

function resetLeaveFlags() {
  leftAfterTransition.value = {
    overlay: false,
    container: false,
  }
}

function tryEndPresent() {
  const {overlay, container} = leftAfterTransition.value
  if (!appLayoutStore.isSearchOverlayOpen && overlay && container) {
    present.value = false
    resetLeaveFlags()
  }
}

function onOverlayAfterLeave() {
  leftAfterTransition.value.overlay = true
  tryEndPresent()
}

function onContainerAfterLeave() {
  leftAfterTransition.value.container = true
  tryEndPresent()
}

watch(
  () => appLayoutStore.isSearchOverlayOpen,
  async (open) => {
    if (open) {
      resetLeaveFlags()
      present.value = true
      await nextTick()
      show.value = true
      return
    }
    show.value = false
  },
)

const {zIndex} = useOverlay({
  element: rootRef,
  isOpen: () => present.value,
  onClose: () => appLayoutStore.closeSearchOverlay(),
})

const offsetTopPx = () => {
  let top = 0
  if (props.hasNativeHeader) {
    top += 36
  }
  return top
}
</script>

<template>
  <div
    v-if="present"
    ref="rootRef"
    class="global-search-overlay-root"
    :style="{
      '--global-search-offset-top': `${offsetTopPx()}px`,
      zIndex: zIndex ?? undefined,
    }"
  >
    <Transition name="overlay-transition" appear @afterLeave="onOverlayAfterLeave">
      <div
        v-if="show"
        class="global-search-overlay-root__backdrop"
        @click="appLayoutStore.closeSearchOverlay()"
      />
    </Transition>
    <Transition name="container-transition" appear @afterLeave="onContainerAfterLeave">
      <div v-if="show" class="global-search-overlay-root__content">
        <HomeSearchComponent/>
      </div>
    </Transition>
  </div>
</template>

<style scoped lang="scss">
  @use '../../../styles/variables' as variables;

  .global-search-overlay-root {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    top: var(--global-search-offset-top, 0px);
    display: flex;
    align-items: stretch;
    justify-content: center;
    box-sizing: border-box;
    pointer-events: none;
  }

  .global-search-overlay-root__backdrop {
    position: absolute;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
    background: oklch(16% 0.01 245 / 0.85);
    pointer-events: all;
  }

  .global-search-overlay-root__content {
    position: relative;
    z-index: 10;
    flex: 1;
    min-width: 0;
    min-height: 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    pointer-events: all;
    overflow: hidden;

    @media (max-width: 960px) {
      padding-top: var(--safe-area-top);
      box-sizing: border-box;
    }
  }

  .container-transition {
    &-enter-active {
      transition: all 0.25s cubic-bezier(0, 0.5, 0, 1.5);
    }

    &-leave-active {
      transition: all 0.25s cubic-bezier(0, 0.5, 0, 1.5);
    }

    &-enter-active,
    &-leave-active {
      transform: none;
    }

    &-enter-from,
    &-leave-to {
      opacity: 0;
      transform: translateY(1rem) scale(1);
    }
  }

  .overlay-transition {
    &-enter-active {
      transition: all 0.25s cubic-bezier(0, 0.5, 0, 1.5);
    }

    &-leave-active {
      transition: all 0.25s cubic-bezier(0, 0.5, 0, 1.5);
    }

    &-enter-from,
    &-leave-to {
      opacity: 0;
      visibility: hidden;
    }
  }
</style>
